/* Impression via un cadre invisible.

   POURQUOI ON N'UTILISE PLUS `srcdoc`. Un cadre rempli par `srcdoc` porte l'URL
   `about:srcdoc`, que le navigateur recopie telle quelle dans l'en-tete des
   pages imprimees : une mention technique en haut d'une piece officielle. En
   ECRIVANT le document dans le cadre, celui-ci herite de l'URL de l'application
   et l'en-tete affiche le <title> qu'on lui donne.

   CE QUE CELA NE FAIT PAS. Cet en-tete reste celui du NAVIGATEUR : il se retire
   dans la boite d'impression, case « En-tetes et pieds de page », et aucun code
   de la page ne peut l'en empecher. On choisit ce qu'il dit, pas s'il parait. */
const echapperTitre = (valeur) =>
  String(valeur ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Ecrit `documentHtml` dans le cadre, puis imprime.
 *
 * L'ECRITURE EST SYNCHRONE, APRES INSERTION DANS LA PAGE. Le cadre doit d'abord
 * etre attache pour avoir un `contentDocument`. Passer par `onload` aurait
 * rouvert un piege : `doc.close()` termine un chargement et redeclenche
 * `onload`, donc l'ecriture en boucle.
 */
function ecrireDansLeCadre(iframe, documentHtml) {
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument;
  if (!doc) return;
  doc.open();
  doc.write(documentHtml);
  doc.close();
  // Au tour de boucle suivant : le navigateur doit avoir mis le document en page,
  // sinon l'impression sort une feuille blanche.
  setTimeout(() => imprimerCadre(iframe), 0);
}

/**
 * Imprime un fragment HTML avec sa feuille de style.
 *
 * @param titre porte par le <title> du document, donc par l'en-tete d'impression
 *        du navigateur. Sans lui, celui-ci affichait « about:srcdoc ».
 */
export function printInIframe(htmlString, cssString, titre) {
  if (!document.body) return;

  const styleTag = cssString ? '<style>' + cssString + '</style>' : '';
  const documentHtml =
    '<!doctype html><html lang="fr"><head><meta charset="utf-8">' +
    '<title>' + echapperTitre(titre || 'Document') + '</title>' +
    styleTag +
    '</head><body>' +
    htmlString +
    '</body></html>';

  ecrireDansLeCadre(cadreInvisible(), documentHtml);
}

/* Cadre invisible pour imprimer sans ouvrir d'onglet. `window.open` affichait le
   document en pleine page avant la boîte d'impression, et laissait l'onglet derrière. */
function cadreInvisible() {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('data-purpose', 'print');
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, {
    position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: 'none', visibility: 'hidden',
  });
  return iframe;
}

/**
 * Déclenche l'impression depuis le cadre, ou rend `false` si le navigateur
 * l'interdit.
 *
 * POURQUOI CE `try`. Un PDF affiché dans un cadre n'est pas rendu par la page :
 * le navigateur y installe sa propre visionneuse, servie depuis une origine
 * interne. Le cadre devient donc CROSS-ORIGIN, et toute lecture de son
 * `contentWindow` lève « Permission denied to access property addEventListener
 * on cross-origin object » - c'est exactement ce qui se produisait sur Firefox
 * au téléchargement d'une offre. L'exception partait avant `print()`, de sorte
 * que l'impression n'avait jamais lieu et que l'utilisateur ne voyait qu'une
 * erreur technique.
 *
 * Le document HTML écrit dans le cadre, lui, partage l'origine de la page et
 * reste accessible : ce repli ne concerne en pratique que les PDF.
 */
function imprimerCadre(iframe, liberer) {
  // Retiré après l'impression, pas avant : certains navigateurs (Firefox) ne bloquent
  // pas sur print() et annuleraient l'impression si le cadre disparaissait tout de suite.
  const retirer = () => { iframe.remove(); liberer?.(); };
  try {
    const win = iframe.contentWindow;
    win.addEventListener('afterprint', () => setTimeout(retirer, 100));
    setTimeout(retirer, 120000);
    win.focus();
    win.print();
    return true;
  } catch {
    retirer();
    return false;
  }
}

/** Imprime un document HTML complet (<!DOCTYPE html>…), deja porteur de son <title>. */
export function imprimerDocument(documentHtml) {
  if (!document.body) return;
  ecrireDansLeCadre(cadreInvisible(), documentHtml);
}

/**
 * Imprime un PDF (Blob) dans la boîte d'impression, sans l'ouvrir dans un onglet.
 *
 * REPLI SUR UN ONGLET QUAND LE NAVIGATEUR REFUSE. Firefox rend les PDF avec sa
 * visionneuse interne, servie depuis une autre origine : le cadre devient
 * inaccessible et l'impression silencieuse est impossible. Plutôt que de laisser
 * l'utilisateur devant une erreur et un document qui ne sort pas, on ouvre alors
 * le PDF dans un onglet, d'où il peut imprimer lui-même.
 *
 * L'URL n'est libérée qu'après un délai dans ce cas : la révoquer tout de suite
 * viderait l'onglet qu'on vient d'ouvrir.
 */
export function imprimerPdf(blob) {
  if (!document.body || !blob) return;
  const url = URL.createObjectURL(blob);
  const iframe = cadreInvisible();
  iframe.onload = () => {
    const imprime = imprimerCadre(iframe, () => URL.revokeObjectURL(url));
    if (imprime) return;
    const onglet = window.open(url, '_blank', 'noopener');
    // Bloqueur de fenêtres : on force alors le téléchargement, pour que le
    // document parvienne malgré tout à l'utilisateur.
    if (!onglet) {
      const lien = document.createElement('a');
      lien.href = url;
      lien.download = 'document.pdf';
      lien.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  iframe.src = url;
  document.body.appendChild(iframe);
}
